import type { Member, OutlookCandidate, OutlookImportResult, ScheduleEvent } from "./types";
import { getAccessToken } from "./auth";

const DEFAULT_API_BASE_URL = "https://oedo-user-scheduler-api.oedo.workers.dev";
export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || DEFAULT_API_BASE_URL).replace(/\/$/, "");

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isMember(value: unknown): value is Member {
  return isRecord(value) && typeof value.id === "string" && typeof value.name === "string";
}

function isScheduleEvent(value: unknown): value is ScheduleEvent {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.title === "string" &&
    typeof value.start === "string" &&
    typeof value.end === "string" &&
    typeof value.allDay === "boolean" &&
    Array.isArray(value.memberIds) &&
    value.memberIds.every((id) => typeof id === "string") &&
    (value.source === "notion" || value.source === "outlook") &&
    typeof value.category === "string" &&
    (value.teamAddresses === undefined ||
      (Array.isArray(value.teamAddresses) &&
        value.teamAddresses.every((address) => typeof address === "string")))
  );
}

function isOutlookCandidate(value: unknown): value is OutlookCandidate {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.title === "string" &&
    typeof value.start === "string" &&
    typeof value.end === "string" &&
    typeof value.allDay === "boolean" &&
    (value.recurring === undefined || typeof value.recurring === "boolean") &&
    Array.isArray(value.suggestedMemberIds) &&
    value.suggestedMemberIds.every((id) => typeof id === "string") &&
    Array.isArray(value.teamAddresses) &&
    value.teamAddresses.every((address) => typeof address === "string")
  );
}

async function fetchJson(
  url: string,
  options: {
    signal?: AbortSignal;
    method?: "GET" | "POST" | "PATCH" | "DELETE";
    body?: unknown;
    interactiveAuth?: boolean;
  } = {},
): Promise<unknown> {
  const accessToken = await getAccessToken(options.interactiveAuth ?? false);
  const response = await fetch(url, {
    signal: options.signal,
    method: options.method ?? "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(options.body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });
  if (!response.ok) throw new Error(`API request failed (${response.status})`);
  return response.json();
}

export type FeedbackType = "bug" | "request" | "question";

export interface FeedbackIssueInput {
  type: FeedbackType;
  title: string;
  description: string;
  reproduction?: string;
  expected?: string;
  pageUrl?: string;
  userAgent?: string;
}

export interface CreatedFeedbackIssue {
  number: number;
  url: string;
}

function isGitHubUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === "github.com";
  } catch {
    return false;
  }
}

export async function submitFeedbackIssue(
  input: FeedbackIssueInput,
): Promise<CreatedFeedbackIssue> {
  const payload = await fetchJson(`${API_BASE_URL}/api/feedback`, {
    method: "POST",
    body: input,
    interactiveAuth: true,
  });
  if (
    !isRecord(payload) ||
    payload.ok !== true ||
    !isRecord(payload.issue) ||
    typeof payload.issue.number !== "number" ||
    typeof payload.issue.url !== "string" ||
    !isGitHubUrl(payload.issue.url)
  ) {
    throw new Error("フィードバックAPIの応答形式が不正です");
  }
  return { number: payload.issue.number, url: payload.issue.url };
}

export async function getMembers(signal: AbortSignal): Promise<Member[]> {
  const payload = await fetchJson(`${API_BASE_URL}/api/members`, { signal });
  if (!isRecord(payload) || !Array.isArray(payload.members) || !payload.members.every(isMember)) {
    throw new Error("メンバーAPIの応答形式が不正です");
  }
  return payload.members;
}

export async function getCapabilities(signal?: AbortSignal): Promise<{ canEditEvents: boolean }> {
  const payload = await fetchJson(`${API_BASE_URL}/api/capabilities`, { signal });
  if (!isRecord(payload) || typeof payload.canEditEvents !== "boolean") {
    throw new Error("権限APIの応答形式が不正です");
  }
  return { canEditEvents: payload.canEditEvents };
}

export async function updateEventParticipants(
  eventId: string,
  memberIds: string[],
): Promise<{ pageId: string; memberIds: string[] }> {
  const payload = await fetchJson(
    `${API_BASE_URL}/api/events/${encodeURIComponent(eventId)}/participants`,
    { method: "PATCH", body: { memberIds } },
  );
  if (
    !isRecord(payload) ||
    payload.ok !== true ||
    typeof payload.pageId !== "string" ||
    !Array.isArray(payload.memberIds) ||
    !payload.memberIds.every((id) => typeof id === "string")
  ) {
    throw new Error("イベント更新APIの応答形式が不正です");
  }
  return { pageId: payload.pageId, memberIds: payload.memberIds };
}

export async function deleteEvent(eventId: string): Promise<{ pageId: string }> {
  const payload = await fetchJson(
    `${API_BASE_URL}/api/events/${encodeURIComponent(eventId)}`,
    { method: "DELETE" },
  );
  if (
    !isRecord(payload) ||
    payload.ok !== true ||
    payload.inTrash !== true ||
    typeof payload.pageId !== "string"
  ) {
    throw new Error("イベント削除APIの応答形式が不正です");
  }
  return { pageId: payload.pageId };
}

export async function getEvents(from: string, to: string, signal: AbortSignal): Promise<ScheduleEvent[]> {
  const query = new URLSearchParams({ from, to });
  const payload = await fetchJson(`${API_BASE_URL}/api/events?${query}`, { signal });
  if (!isRecord(payload) || !Array.isArray(payload.events) || !payload.events.every(isScheduleEvent)) {
    throw new Error("イベントAPIの応答形式が不正です");
  }
  return payload.events;
}

/** Notion を経由しない生の Outlook 予定。表示専用なので live 印を付けて返す。 */
export async function getRawOutlookEvents(
  from: string,
  to: string,
  memberIds: string[],
  signal?: AbortSignal,
): Promise<ScheduleEvent[]> {
  if (memberIds.length === 0) return [];
  const query = new URLSearchParams({ from, to, memberIds: memberIds.join(",") });
  const payload = await fetchJson(`${API_BASE_URL}/api/outlook/events?${query}`, { signal });
  if (!isRecord(payload) || !Array.isArray(payload.events) || !payload.events.every(isScheduleEvent)) {
    throw new Error("Outlook予定APIの応答形式が不正です");
  }
  return payload.events.map((event) => ({ ...event, live: true }));
}

export async function getOutlookCandidates(
  memberId: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<OutlookCandidate[]> {
  const query = new URLSearchParams({ memberId, from, to });
  const payload = await fetchJson(`${API_BASE_URL}/api/outlook/candidates?${query}`, { signal });
  if (!isRecord(payload) || !Array.isArray(payload.events) || !payload.events.every(isOutlookCandidate)) {
    throw new Error("Outlook候補APIの応答形式が不正です");
  }
  return payload.events;
}

export async function importOutlookEvent(input: {
  memberId: string;
  eventId: string;
  participantsByDate: Record<string, string[]>;
}): Promise<OutlookImportResult> {
  const payload = await fetchJson(`${API_BASE_URL}/api/outlook/import`, {
    method: "POST",
    body: input,
  });
  if (
    !isRecord(payload) ||
    payload.ok !== true ||
    typeof payload.createdCount !== "number" ||
    typeof payload.updatedCount !== "number" ||
    !Array.isArray(payload.pageIds) ||
    !payload.pageIds.every((id) => typeof id === "string")
  ) {
    throw new Error("Outlook登録APIの応答形式が不正です");
  }
  return {
    ok: true,
    createdCount: payload.createdCount,
    updatedCount: payload.updatedCount,
    pageIds: payload.pageIds,
  };
}
